import React, { Component } from "react";
import memoize from "memoize-one";
import isEqual from "lodash.isequal";

import { getDevicesAvailableToClient, reassignClientsDevices } from "../../../../../api";
import { FilteredList, DevicesFilter, DevicesPickerTable, Dialog } from "../../../../../components";
import Auth from "../../../../../Auth";
import ReassignDetailsDialog from "./ReassignDetailsDialog";

const filterDevices = (data, filters) => {
  if (!filters) {
    return data;
  }

  const { option, filterValue } = filters;

  return data.filter(x => {
    if (filterValue) {
      if (x.isGroup) {
        return false; // don't take groups into account
      }
      
      const strFilter = filterValue.toLowerCase();
      const isMatch = [x.description, x.make, x.model, x.vrn, x.vehicleNumber, x.serialNumber, x.imei, x.ccid, x.simCardPhoneNumber, x.id + ""]
        .filter(field => field?.toLowerCase()?.includes(strFilter))
        .length > 0;
  
      if (!isMatch) {
        return false;
      }
    }

    switch (option) {
      case "1":
        return !x.clientId;
      case "2":
        return x.selected;
      case "3":
      default:
        return true;
    }
  });
};

const sortDevices = data => {
  return data.sort((a, b) => {
    if (a.group && !b.group) return 1;
    if (!a.group && b.group) return -1;
    if (a.group && a.group.id !== b.group.id) return a.group.name.localeCompare(b.group.name, "pl", { numeric: true });

    return (a.description || "").localeCompare(b.description || "", "pl", { numeric: true }); // sort by device description ASC
  });
};

const groupDevices = (data, filterValue) => {
  if (!data) {
    return [];
  }

  if (filterValue) { // don't return groups when filtering
    return data;
  }

  return data.reduce((a, x, i, arr) => { // inserts rows with group name before the items that belong to that group
    if (i === 0) {
      if (!x.group) {
        a.push({ id: 0, name: "Brak grupy", isGroup: true });
      }
      else {
        a.push({ ...x.group, isGroup: true });
      }
    }
    else if (arr[i - 1].group?.id !== x.group?.id) { // switched to different group
      a.push({ ...x.group, isGroup: true });
    }
    a.push(x);

    return a;
  }, []);
};

const getChangedDevices = (devicesGroups, selectedDevices) => {
  return devicesGroups.reduce((a, x) => {
    if (x.isGroup) {
      return a;
    }

    const selected = !!selectedDevices[x.id];
    if (selected && !x.selected) {
      a.selected.push(x);
    }
    else if (!selected && x.selected) {
      a.deselected.push(x);
    }

    return a;
  }, { selected: [], deselected: [] });
};

class ClientDevices extends Component {
  state = {
    showDeassignConfirmationDialog: false,
    confirmationDetails: {}
  };

  onListChangedCallback = (filteredList, init, fullList) => {
    if (init) {
      const selectedElements = fullList.reduce((a, x) => {
        if (x.selected) {
          a.push(x.id);
        }

        return a;
      }, []);
  
      if (selectedElements.length) {
        this.selectElements(selectedElements, true, true);
      }
    }
  };

  memoizeGroupedDevices = memoize((devices, filterValue) => {
    return groupDevices(devices, filterValue);
  }, isEqual);

  saveEndpoint = (selected, deselected, { deassignFromPreviousClientUsers = false, autoAssignDevices = false }) => {
    return reassignClientsDevices({
      clientId: this.props.client.id,
      addIds: selected.map(x => x.id),
      removeIds: deselected.map(x => x.id),
      deassignFromPreviousClientUsers,
      autoAssignDevices,
    });
  };

  showConfirmationDialog = (isAssignedToDifferentClientsUsers, currentClientHasAutoAssignUsers) => {
    return new Promise(resolve => {
      this.setState(() => ({
        showDeassignConfirmationDialog: true,
        dialogResolve: resolve,
        confirmationDetails: { isAssignedToDifferentClientsUsers, currentClientHasAutoAssignUsers },
      }));
    });
  };

  trySave = (selected, deselected) => async () => {
    const isAssignedToDifferentClientsUsers = selected.some(x => x.clientId !== null) || !!deselected.length; // trying to reassign from a different client or deassign
    const currentClientHasAutoAssignUsers = Object.values(this.props.client.users).some(x => x.autoAssignDevices);

    if (isAssignedToDifferentClientsUsers || currentClientHasAutoAssignUsers) {
      try {
        const result = await this.showConfirmationDialog(isAssignedToDifferentClientsUsers, currentClientHasAutoAssignUsers);

        if (result) {
          return await this.saveEndpoint(selected, deselected, result);
        }
      }
      catch (e) {
        console.error(e);
      }
    }
    else {
      return await this.saveEndpoint(selected, deselected, {});
    }
  };

  onDialogAccept = params => {
    const { dialogResolve } = this.state;
    if (dialogResolve) {
      this.setState(() => ({ showDeassignConfirmationDialog: false, dialogResolve: null, confirmationDetails: {} }), () => dialogResolve(params));
    }
  };

  onDialogReject = () => {
    const { dialogResolve } = this.state;
    if (dialogResolve) {
      this.setState(() => ({ showDeassignConfirmationDialog: false, dialogResolve: null, confirmationDetails: {} }), () => dialogResolve(null));
    }
  };

  render() {
    const { client } = this.props;
    const { showDeassignConfirmationDialog, confirmationDetails } = this.state;
    const clientId = client.id;
    const _this = this;

    return (
      <Auth.Consumer>
        {({ hasPermission }) => (
          <FilteredList multiselect dependencyId={clientId} endpoint={getDevicesAvailableToClient} data={{ clientId }} keyField="id" listFilter={filterDevices} listSorter={sortDevices} listChangedCallback={this.onListChangedCallback}>
            {({ fullList, filteredList, selectedElements, isFetching, filterPhrase: { option, filterValue } }, { refresh, refreshComponents, onFilterChanged, selectElement, selectAll, deselectAll, areAllSelected, selectedCount, selectElements }) => {
              _this.selectElements = selectElements;
              const devicesGroups = this.memoizeGroupedDevices(filteredList, filterValue);
              const { selected, deselected } = getChangedDevices(fullList, selectedElements);
              const isDirty = !!(selected.length || deselected.length);
              const canAssignDevicesToClients = hasPermission("managingClientsDevices");
              const canAccessDeveloperSettings = hasPermission("accessToDeveloperSettings");
              const canAccessSimCards = hasPermission("managingSimCards");

              return (
                <div style={{ overflowY: "hidden", margin: "-1.25rem", height: "calc(100% - 1.5rem)" }}>
                  <div style={{ margin: "1.25rem", height: "calc(100% - 2.5rem)" }}>
                    <DevicesFilter
                      defaultValue={{ option: "2", filterValue: "" }}
                      unassignedEntries
                      assignedEntries
                      allEntries
                      filterField
                      option={option}
                      onChange={onFilterChanged}
                      canAssignDevices={canAssignDevicesToClients}
                      kind={1}
                      clientId={clientId}
                    />

                    <DevicesPickerTable
                      isFetching={isFetching} devices={fullList} devicesGroups={devicesGroups} refreshDevicesList={refresh}
                      selectedDevices={selectedElements} selectElement={selectElement} selectElements={selectElements} selected={selected}
                      deselected={deselected} isDirty={isDirty} canAssignDevices={canAssignDevicesToClients} canAccessDeveloperSettings={canAccessDeveloperSettings}
                      save={this.trySave(selected, deselected)} canAccessSimCards={canAccessSimCards} context={`${clientId}_${option}`} filterValue={filterValue}
                    />

                    <ReassignDetailsDialog
                      isOpen={showDeassignConfirmationDialog}
                      accept={this.onDialogAccept}
                      reject={this.onDialogReject}
                      showDeassignFromPreviousClientUsersCheckbox={confirmationDetails?.isAssignedToDifferentClientsUsers}
                      showAutoAssignDevicesCheckbox={confirmationDetails?.currentClientHasAutoAssignUsers}
                    />
                  </div>
                </div>
              );
            }}
          </FilteredList>
        )}
      </Auth.Consumer>
    );
  }
}

export default ClientDevices;
